import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { ContributionDetailDialog } from '@/components/giving/contribution-detail-dialog';
import { FundSummaryDialog } from '@/components/giving/fund-summary-dialog';
import {
  AuditTrailPanel,
  type AuditRelated,
} from '@/components/shared/audit-trail-panel';
import {
  DateRangeFilter,
  resolvePreset,
  type DateRange,
} from '@/components/shared/date-range-filter';
import { FilterBar } from '@/components/shared/filter-bar';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { Listbox } from '@/components/ui/listbox';
import { api } from '@/lib/api';
import { useChurchStore } from '@/stores/church-store';
import { usePreferences } from '@/stores/preferences-store';

import {
  type AuditLogRow,
  FINANCIAL_AUDIT_ACTIONS,
  FINANCIAL_AUDIT_ENTITY_TYPES,
  presentAuditEntry,
} from './audit-format';

const GENERAL_AUDIT_ACTIONS = [
  'member.create',
  'member.promote',
  'member.inactive',
  'member.restore',
  'member.transfer',
  'member.deceased',
  'member.import',
  'member.export',
  'member.merge',
  'member.delete',
  'member.anonymise',
  'member.link_user',
  'member.family_link.create',
  'member.family_link.remove',
  'member_join_request.submit',
  'member_join_request.approve',
  'member_join_request.merge',
  'member_join_request.reject',
  'login_request.approve',
  'login_request.reject',
  'login_request.consume',
  'prayer.read',
  'pastoral.counseling.read',
  'branch.create',
  'branch.switch',
  'branch.set_as_head',
  'branch.deactivate',
  'branch.reactivate',
  'totp.enable',
  'totp.disable',
  'report.export',
  'user.account.delete',
  'platform.church.suspended',
  'platform.church.reactivated',
  'platform.church.payments_suspended',
  'platform.church.payments_resumed',
  'platform.impersonation.started',
  'platform.impersonation.ended',
  'platform.subscription.updated',
  'platform.user.updated',
  'platform.email.route_updated',
  'platform.sms.transport_updated',
  'platform.giving_payments.providers_updated',
  'platform.app_version.updated',
  'platform.console_session.updated',
  'platform.content_default_images.updated',
  'platform.job.dead_letter_resolved',
  'platform.job.dead_letter_retried',
  'platform.coupon.created',
  'platform.coupon.updated',
] as const;

const GENERAL_AUDIT_ENTITY_TYPES = [
  'member',
  'login_request',
  'prayer_request',
  'church',
  'user',
  'report',
  'subscription',
  'platform_setting',
  'platform_impersonation',
  'job_dead_letter',
  'billing_coupon',
  'member_join_request',
] as const;

const AUDIT_ACTIONS = [...GENERAL_AUDIT_ACTIONS, ...FINANCIAL_AUDIT_ACTIONS] as const;
const AUDIT_ENTITY_TYPES = [
  ...GENERAL_AUDIT_ENTITY_TYPES,
  ...FINANCIAL_AUDIT_ENTITY_TYPES,
] as const;

export const AuditLogPage = () => {
  const { t } = useTranslation('audit');
  const { t: tCommon } = useTranslation();
  const { t: tGiving } = useTranslation('giving');
  const navigate = useNavigate();
  const timezone = usePreferences((state) => state.timezone);
  const locale = usePreferences((state) => state.locale);
  const church = useChurchStore((state) => state.church);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');
  const [contributionId, setContributionId] = useState<string | null>(null);
  const [fundId, setFundId] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>(() => ({
    preset: 'allTime',
    ...resolvePreset('allTime', timezone),
  }));

  const openRelated = (related: AuditRelated) => {
    if (related.kind === 'contribution' && related.id) {
      setContributionId(related.id);
      return;
    }
    if (related.kind === 'fund' && related.id) {
      setFundId(related.id);
      return;
    }
    if (related.href) {
      void navigate(related.href);
    }
  };

  const dateFrom = dateRange.from;
  const dateTo = dateRange.to;
  const appliedCount = [
    action,
    entityType,
    dateRange.preset !== 'allTime' ? dateRange.preset : null,
  ].filter(Boolean).length;

  const actionOptions = useMemo(
    () => [
      { value: '', label: t('filters.allActions') },
      ...AUDIT_ACTIONS.map((value) => ({
        value,
        label: t(`actions.${value}`),
      })),
    ],
    [t],
  );

  const entityTypeOptions = useMemo(
    () => [
      { value: '', label: t('filters.allTypes') },
      ...AUDIT_ENTITY_TYPES.map((value) => ({
        value,
        label: t(`entityTypes.${value}`),
      })),
    ],
    [t],
  );

  const query = useQuery({
    queryKey: ['audit-logs', page, pageSize, action, entityType, dateFrom, dateTo],
    queryFn: () =>
      api.get<{ data: AuditLogRow[]; page: { totalItems: number; totalPages: number } }>(
        '/audit/logs',
        {
          query: {
            page,
            pageSize,
            ...(action ? { action } : {}),
            ...(entityType ? { entityType } : {}),
            ...(dateFrom ? { dateFrom } : {}),
            ...(dateTo ? { dateTo } : {}),
            sort: '-createdAt',
          },
        },
      ),
  });

  const entries = useMemo(
    () =>
      (query.data?.data ?? []).map((row) =>
        presentAuditEntry(row, {
          t,
          tGiving,
          locale,
          defaultCurrency: church?.baseCurrency ?? null,
        }),
      ),
    [church?.baseCurrency, locale, query.data, t, tGiving],
  );

  const pageMeta = query.data?.page;

  return (
    <div>
      <PageHeader
        title={t('title')}
        description={t('description')}
        tipId="settings.audit.scope"
      />

      <FilterBar
        appliedCount={appliedCount}
        onClearAll={() => {
          setAction('');
          setEntityType('');
          setDateRange({
            preset: 'allTime',
            ...resolvePreset('allTime', timezone),
          });
          setPage(1);
        }}
        labels={{
          filters: t('filters.title'),
          clearAll: t('filters.clearAll'),
          applied: (count) => t('filters.applied', { count }),
        }}
      >
        {({ popoverPortal }) => (
          <>
            <div className="w-56 min-w-[12rem]">
              <Listbox
                value={action}
                onChange={(value) => {
                  setAction(value ?? '');
                  setPage(1);
                }}
                options={actionOptions}
                placeholder={t('filters.allActions')}
                searchable
                searchPlaceholder={t('filters.searchActions')}
                emptyMessage={t('filters.noMatchingActions')}
                aria-label={t('filters.action')}
                popoverPortal={popoverPortal}
              />
            </div>
            <div className="w-48 min-w-[10rem]">
              <Listbox
                value={entityType}
                onChange={(value) => {
                  setEntityType(value ?? '');
                  setPage(1);
                }}
                options={entityTypeOptions}
                placeholder={t('filters.allTypes')}
                aria-label={t('filters.entityType')}
                popoverPortal={popoverPortal}
              />
            </div>
            <DateRangeFilter
              value={dateRange}
              onChange={(range) => {
                setDateRange(range);
                setPage(1);
              }}
              popoverPortal={popoverPortal}
              labels={{
                today: tCommon('date.today'),
                yesterday: tCommon('date.yesterday'),
                thisWeek: tCommon('date.thisWeek'),
                lastWeek: tCommon('date.lastWeek'),
                thisMonth: tCommon('date.thisMonth'),
                lastMonth: tCommon('date.lastMonth'),
                thisQuarter: tCommon('date.thisQuarter'),
                thisYear: tCommon('date.thisYear'),
                lastYear: tCommon('date.lastYear'),
                allTime: tCommon('date.allTime'),
                custom: tCommon('date.custom'),
                from: t('filters.dateFrom'),
                to: t('filters.dateTo'),
              }}
            />
          </>
        )}
      </FilterBar>

      <AuditTrailPanel
        title={t('title')}
        entries={entries}
        loading={query.isLoading}
        emptyTitle={t('empty.title')}
        emptyBody={t('empty.body')}
        emptyValueLabel={t('emptyValue')}
        onOpenRelated={openRelated}
      />

      <ContributionDetailDialog
        contributionId={contributionId}
        open={Boolean(contributionId)}
        onOpenChange={(open) => {
          if (!open) setContributionId(null);
        }}
      />
      <FundSummaryDialog
        fundId={fundId}
        open={Boolean(fundId)}
        onOpenChange={(open) => {
          if (!open) setFundId(null);
        }}
      />

      {pageMeta ? (
        <Pagination
          className="mt-4"
          page={page}
          pageSize={pageSize}
          totalItems={pageMeta.totalItems}
          totalPages={pageMeta.totalPages}
          onPageChange={setPage}
          onPageSizeChange={(next) => {
            setPageSize(next);
            setPage(1);
          }}
          labels={{
            showing: ({ from, to, total }) =>
              tCommon('pagination.showing', { from, to, total }),
            perPage: tCommon('pagination.perPage'),
            first: tCommon('pagination.first'),
            previous: tCommon('pagination.previous'),
            next: tCommon('pagination.next'),
            last: tCommon('pagination.last'),
          }}
          disabled={query.isFetching}
        />
      ) : null}
    </div>
  );
};
